import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useTranslation } from 'react-i18next';
import { CourtDropZone, MatchItem, type GridMatchPlacementBehavior } from './Item';
import './Schedule.scss';
import { useAppSelector } from '../../../../../hooks/redux';
import { formatInTimeZone } from 'date-fns-tz';
import {
  getDateString,
  getHhmmTimeString,
  getMatchesForTournamentDay,
  getTournamentDayDisplayBounds,
  getTournamentTimezone,
} from '../schedule.helpers';
import {
  ICourtSchedule,
  IDaySchedule,
  IFreeSlotItem,
  IMatchScheduleItem,
  IScheduleConstraints,
  IScheduleDayConstraints,
  ITournamentSchedule,
  isFreeSlot,
} from '../../../../../types/schedule';
import { Class } from '../../../../../types/tournament';
import { flattenScheduledMatches } from '../../../../../features/schedule/scheduleOverlap';
import { getPlacementConflictIndex } from '../../../../../features/schedule/placementConflictIndexCache';
import { selectUnscheduledMatches } from '../../../../../features/schedule/scheduleSelectors';
import { MATCH_RENDER_BUFFER_PX } from './performance';
import {
  buildSwapModifierPreviewDimsMap,
  type SwapModifierPreviewDims,
} from './scheduleGridSwapPreview';
import { getPlacementDayIndex } from './schedulePlacementIndex';
import { SchedulePlacementOverlay } from './SchedulePlacementOverlay';
import {
  SchedulePlacementActionsProvider,
  SchedulePlacementSelectionProvider,
  type SchedulePlacementActionsContextValue,
  type SchedulePlacementSelectionContextValue,
} from './SchedulePlacementContext';
import type { PlacementSessionIndex } from './schedulePlacementIndex';
import {
  getGridMatchHeightCalc,
  getGridMatchTopCalc,
  getMatchMinutesFromDayStart,
} from './scheduleMatchLayout';
import { getScheduleZoomTier, useScheduleZoom } from '../NewScheduleLayout/ScheduleZoomContext';
import { applyScheduleZoomCssVars } from '../NewScheduleLayout/scheduleZoomCss';
import {
  registerScheduleZoomCanvas,
  unregisterScheduleZoomCanvas,
  updateScheduleZoomCanvasParams,
} from '../NewScheduleLayout/scheduleZoomCanvasRegistry';
import {
  selectScheduleMutationBlocked,
  selectScheduleClassIdToObject,
  selectScheduleColumnWidth,
  selectScheduleConstraints,
  selectScheduleData,
  selectScheduleItemScaleMultiplier,
  selectScheduleTournamentInfo,
  selectDisplaySchedule,
  selectScheduleSelectedMatchIds,
} from '../../../../../features/schedule/scheduleSelectors';
import { MIN_SCHEDULE_BULK_ADJUST } from '../../../../../features/schedule/scheduleSelectionConstants';
import {
  buildGroupDragOverlayLayout,
  type GroupDragOverlayLayout,
} from './scheduleGroupDragOverlay';
import { selectScheduleGridDayInput } from '../../../../../features/schedule/scheduleGridSelectors';
import {
  clampScheduleGridScroll,
  computeScheduleZoomFloorsForDay,
  getScheduleCanvasHeightPx,
  getScheduleCanvasWidthPx,
  getScheduleEffectiveCourtWidthPx,
  getScheduleLaidOutRowHeightPx,
  type ScheduleZoomFloors,
} from '../../../../../features/schedule/scheduleZoomBounds';

const FREE_SLOT_MINUTES = 5;
const MS_PER_MINUTE = 60000;
const TIME_COLUMN_WIDTH = 75;
const HEADER_HEIGHT = 36;
const MAX_COURT_GAP_PX = 5;

/** Space between virtual court columns; scales down with column width (was fixed 5px). */
const getCourtGapPx = (courtWidth: number) =>
  Math.max(0, Math.min(MAX_COURT_GAP_PX, Math.floor(courtWidth / 45)));

const VERTICAL_OVERSCAN = 12;
const HORIZONTAL_OVERSCAN = 3;

type MatchRange = {
  item: IMatchScheduleItem;
  startMs: number;
  endMs: number;
};

type CourtGridModel = {
  courtSchedule: ICourtSchedule;
  matchRanges: MatchRange[];
};

type DayGridModel = {
  day: IDaySchedule;
  dayStartMs: number;
  dayEndMs: number;
  slotCount: number;
  constraintDayDateStr: string;
  courts: CourtGridModel[];
};

const getMatchStartMs = (match: IMatchScheduleItem) =>
  match.scheduleDate ? new Date(match.scheduleDate).getTime() : 0;

const MAX_MATCH_SPAN_MINUTES = 8 * 60;

const getMatchEndMs = (
  match: IMatchScheduleItem,
  startMs: number,
  dayEndMs: number
) => {
  let endMs = match.scheduleEndDate
    ? new Date(match.scheduleEndDate).getTime()
    : startMs + (match.duration || 20) * MS_PER_MINUTE;

  if (!Number.isFinite(endMs) || endMs <= startMs) {
    endMs = startMs + (match.duration || 20) * MS_PER_MINUTE;
  }

  const maxEndMs = Math.min(
    dayEndMs,
    startMs + MAX_MATCH_SPAN_MINUTES * MS_PER_MINUTE
  );
  return Math.min(endMs, maxEndMs);
};

const getFallbackDayBounds = (day: IDaySchedule) => {
  const scheduledItems = day.courts
    .flatMap((court) => court.matches)
    .filter(
      (item): item is IMatchScheduleItem =>
        !!item.scheduleDate && !isFreeSlot(item)
    );
  const firstStartMs = Math.min(
    ...scheduledItems.map((item) => new Date(item.scheduleDate!).getTime())
  );
  const lastEndMs = Math.max(
    ...scheduledItems.map((item) => {
      const startMs = new Date(item.scheduleDate!).getTime();
      if (item.scheduleEndDate) return new Date(item.scheduleEndDate).getTime();
      return startMs + (item.duration || FREE_SLOT_MINUTES) * MS_PER_MINUTE;
    })
  );

  return {
    dayStartMs: Number.isFinite(firstStartMs) ? firstStartMs : Date.now(),
    dayEndMs: Number.isFinite(lastEndMs) ? lastEndMs : Date.now() + 60 * MS_PER_MINUTE,
  };
};

const buildCourtGridModel = (
  courtSchedule: ICourtSchedule,
  dayStartMs: number,
  dayEndMs: number
): CourtGridModel => ({
  courtSchedule,
  matchRanges: courtSchedule.matches
    .filter((item): item is IMatchScheduleItem => !isFreeSlot(item) && !!item.scheduleDate)
    .map((item) => {
      const startMs = getMatchStartMs(item);
      const endMs = getMatchEndMs(item, startMs, dayEndMs);

      return {
        item,
        startMs,
        endMs,
      };
    }),
});

type CourtGridCacheEntry = {
  courtScheduleRef: ICourtSchedule;
  dayStartMs: number;
  dayEndMs: number;
  model: CourtGridModel;
};

const getCourtGridModel = (
  cache: Map<string, CourtGridCacheEntry>,
  dayKey: number,
  courtSchedule: ICourtSchedule,
  dayStartMs: number,
  dayEndMs: number
): CourtGridModel => {
  const key = `${dayKey}:${courtSchedule.court.id}`;
  const cached = cache.get(key);
  if (
    cached &&
    cached.courtScheduleRef === courtSchedule &&
    cached.dayStartMs === dayStartMs &&
    cached.dayEndMs === dayEndMs
  ) {
    return cached.model;
  }

  const model = buildCourtGridModel(courtSchedule, dayStartMs, dayEndMs);
  cache.set(key, { courtScheduleRef: courtSchedule, dayStartMs, dayEndMs, model });
  return model;
};

const buildDayGridModel = (
  day: IDaySchedule,
  dayConstraints: IScheduleDayConstraints | undefined,
  courtGridCache: Map<string, CourtGridCacheEntry>,
  scheduleConstraints: IScheduleConstraints | null | undefined,
  timeZone: string,
  allScheduledMatches: IMatchScheduleItem[] = []
): DayGridModel => {
  const fallbackBounds = getFallbackDayBounds(day);
  let dayStartMs = fallbackBounds.dayStartMs;
  let dayEndMs = fallbackBounds.dayEndMs;
  let constraintDayDateStr = getDateString(new Date(dayStartMs).toISOString(), timeZone);

  if (dayConstraints && scheduleConstraints) {
    const matchesOnDay = getMatchesForTournamentDay(
      dayConstraints,
      allScheduledMatches,
      scheduleConstraints,
      timeZone
    );
    const displayBounds = getTournamentDayDisplayBounds(
      dayConstraints,
      matchesOnDay,
      scheduleConstraints,
      timeZone
    );
    dayStartMs = displayBounds.effectiveStartMs;
    dayEndMs = displayBounds.effectiveEndMs;
    constraintDayDateStr = getDateString(dayConstraints.startTime, timeZone);
  } else if (dayConstraints) {
    dayStartMs = new Date(dayConstraints.startTime).getTime();
    dayEndMs = new Date(dayConstraints.endTime).getTime();
    constraintDayDateStr = getDateString(dayConstraints.startTime, timeZone);
  }

  const slotCount = Math.max(
    1,
    Math.ceil((dayEndMs - dayStartMs) / (FREE_SLOT_MINUTES * MS_PER_MINUTE))
  );

  return {
    day,
    dayStartMs,
    dayEndMs,
    slotCount,
    constraintDayDateStr,
    courts: day.courts.map((courtSchedule) =>
      getCourtGridModel(courtGridCache, day.dayKey, courtSchedule, dayStartMs, dayEndMs)
    ),
  };
};

const formatSlotTimeLabel = (
  slotStartIso: string,
  constraintDayDateStr: string,
  timeZone: string
): string => {
  const slotDateStr = getDateString(slotStartIso, timeZone);
  if (slotDateStr !== constraintDayDateStr) {
    return formatInTimeZone(new Date(slotStartIso), timeZone, 'MMM d HH:mm');
  }
  return getHhmmTimeString(slotStartIso, timeZone);
};

const getCourtDropZoneId = (dayKey: number, courtId: number) =>
  `court-drop-zone-${dayKey}-${courtId}`;

const EMPTY_SCHEDULED_MATCHES: IMatchScheduleItem[] = [];

export type ScheduleEditorProps = {
  /** Day scope for grid; reads courts via `selectScheduleGridDayInput` when set. */
  gridDayKey?: number;
  scheduleItemScaleMultiplier?: number;
  scheduleColumnWidth?: number;
  /** When set, used instead of Redux `displaySchedule` (avoids extra store commits on swap). */
  displaySchedule?: ITournamentSchedule | null;
  pendingPlaceMatchId?: number | null;
  onPlaceAtFreeSlot?: (slot: IFreeSlotItem) => void;
  /** Keyboard focus slot during click-to-place (arrow keys + Enter). */
  focusedPlacementSlot?: IFreeSlotItem | null;
  /** Keyboard swap-target focus while Ctrl/Cmd held during click-to-place. */
  focusedSwapTargetMatchId?: number | null;
  /** Match being dragged or selected for placement; used to paint valid drop start slots. */
  activePlacementMatch?: IMatchScheduleItem | null;
  /** Click a scheduled match to swap with the match being placed or dragged; Ctrl/Cmd only affects target preview. */
  onSwapPendingWithScheduled?: (targetMatch: IMatchScheduleItem) => void | Promise<void>;
  /** Whether pending placement was chosen from the unscheduled list or from a scheduled grid match. */
  pendingPlacementSource?: 'unscheduled' | 'scheduled_grid' | null;
  /** True while Ctrl (Windows) or Cmd (Mac) is held during click-to-place. */
  swapModifierHeld?: boolean;
  /** True while a schedule match is being dragged (used with Ctrl/Cmd swap preview on the grid). */
  scheduleDragActive?: boolean;
  /** Origin of the dragged match (`scheduled` vs unscheduled), when `scheduleDragActive`. */
  dragPlacementSource?: 'unscheduled' | 'scheduled' | null;
  /** Bulk group drag: all selected match ids moving together. */
  groupDragMatchIds?: number[] | null;
  /** Bulk group drag: id of the match being dragged (overlay anchor). */
  groupDragAnchorMatchId?: number | null;
  /** Single-click a scheduled match on the grid to select it for click-to-place (move). */
  onSelectGridMatchForPlacement?: (matchId: number) => void;
  /** Reports viewport-based zoom floors for this day (merged upstream when multiple days). */
  onZoomFloorsChange?: (dayKey: number, floors: ScheduleZoomFloors) => void;
  /** Ctrl/Cmd held — hoisted from ScheduleLayout to avoid per-match window listeners. */
  dragModifierBlocked?: boolean;
  /** Shift held — marquee select on grid; disables match drag/click select. */
  shiftModifierHeld?: boolean;
  /** Deferred placement index is ready (highlights + keyboard context). */
  placementSessionReady?: boolean;
  /** Precomputed placement bands + valid slots for the active session. */
  placementSessionIndex?: PlacementSessionIndex | null;
  /** Highlight matches involved in scheduling conflicts (canonical scan). */
  showScheduleConflicts?: boolean;
  conflictMatchIdList?: number[];
  blockInvalidPlacement?: boolean;
  focusedConflictMatchId?: number | null;
  focusedConflictPeerIds?: number[];
  conflictFocusPulseGeneration?: number;
};

export function dayGridModelPropsAreEqual(
  prev: { dayModel: DayGridModel; scale: number; courtWidth: number },
  next: { dayModel: DayGridModel; scale: number; courtWidth: number }
): boolean {
  if (prev.scale !== next.scale || prev.courtWidth !== next.courtWidth) return false;
  if (prev.dayModel === next.dayModel) return true;
  if (prev.dayModel.day !== next.dayModel.day) return false;
  if (prev.dayModel.courts.length !== next.dayModel.courts.length) return false;
  for (let i = 0; i < prev.dayModel.courts.length; i++) {
    if (prev.dayModel.courts[i] !== next.dayModel.courts[i]) return false;
  }
  return (
    prev.dayModel.dayStartMs === next.dayModel.dayStartMs &&
    prev.dayModel.dayEndMs === next.dayModel.dayEndMs &&
    prev.dayModel.slotCount === next.dayModel.slotCount
  );
}

export const Schedule = ({
  gridDayKey,
  scheduleItemScaleMultiplier: scheduleItemScaleMultiplierProp,
  scheduleColumnWidth: scheduleColumnWidthProp,
  displaySchedule: displayScheduleProp,
  pendingPlaceMatchId = null,
  onPlaceAtFreeSlot,
  focusedPlacementSlot = null,
  focusedSwapTargetMatchId = null,
  activePlacementMatch = null,
  onSwapPendingWithScheduled,
  pendingPlacementSource = null,
  swapModifierHeld = false,
  scheduleDragActive = false,
  dragPlacementSource = null,
  groupDragMatchIds = null,
  groupDragAnchorMatchId = null,
  onSelectGridMatchForPlacement,
  onZoomFloorsChange,
  dragModifierBlocked = false,
  shiftModifierHeld = false,
  placementSessionReady = false,
  placementSessionIndex = null,
  showScheduleConflicts = false,
  conflictMatchIdList = [],
  blockInvalidPlacement = false,
  focusedConflictMatchId = null,
  focusedConflictPeerIds = [],
  conflictFocusPulseGeneration = 0,
}: ScheduleEditorProps = {}) => {
  const gridInput = useAppSelector((state) =>
    gridDayKey != null ? selectScheduleGridDayInput(state, gridDayKey) : null
  );
  const displayScheduleFromStore = useAppSelector(selectDisplaySchedule);
  const displaySchedule =
    displayScheduleProp !== undefined
      ? displayScheduleProp
      : gridInput
        ? { days: [gridInput.day] } as ITournamentSchedule
        : displayScheduleFromStore;
  const scheduleConstraints = useAppSelector(selectScheduleConstraints);
  const fullSchedule = useAppSelector(selectScheduleData);
  const scheduleItemScaleMultiplierFromStore = useAppSelector(selectScheduleItemScaleMultiplier);
  const scheduleColumnWidthFromStore = useAppSelector(selectScheduleColumnWidth);
  const scheduleItemScaleMultiplier =
    scheduleItemScaleMultiplierProp ?? scheduleItemScaleMultiplierFromStore;
  const scheduleColumnWidth = scheduleColumnWidthProp ?? scheduleColumnWidthFromStore;
  const tournamentInfo = useAppSelector(selectScheduleTournamentInfo);
  const classIdToObject = useAppSelector(selectScheduleClassIdToObject);
  const scheduleMutationBlocked = useAppSelector(selectScheduleMutationBlocked);
  const selectedMatchIds = useAppSelector(selectScheduleSelectedMatchIds);
  const placeModeActive = pendingPlaceMatchId != null && typeof onPlaceAtFreeSlot === 'function';
  const swapContextActive = placeModeActive || scheduleDragActive;
  const courtGridCacheRef = useRef(new Map<string, CourtGridCacheEntry>());

  const placementActionsValue = useMemo<SchedulePlacementActionsContextValue>(
    () => ({
      swapModifierHeld,
      swapContextActive,
      activePlacementMatchId: activePlacementMatch?.id ?? null,
      focusedSwapTargetMatchId: focusedSwapTargetMatchId ?? null,
      onSwapWithScheduledMatch: onSwapPendingWithScheduled,
      onSelectGridMatchForPlacement: onSelectGridMatchForPlacement,
      placementDragDisabled: placeModeActive,
      dragModifierBlocked,
      scheduleMutationBlocked,
    }),
    [
      swapModifierHeld,
      swapContextActive,
      activePlacementMatch?.id,
      focusedSwapTargetMatchId,
      onSwapPendingWithScheduled,
      onSelectGridMatchForPlacement,
      placeModeActive,
      dragModifierBlocked,
      scheduleMutationBlocked,
    ]
  );

  const placementSelectionValue = useMemo<SchedulePlacementSelectionContextValue>(
    () => ({
      selectedGridMatchId:
        pendingPlacementSource === 'scheduled_grid' ? pendingPlaceMatchId : null,
    }),
    [pendingPlacementSource, pendingPlaceMatchId]
  );

  const timeZone = getTournamentTimezone(tournamentInfo?.country, tournamentInfo?.timeZone);
  const allScheduledMatchesForBounds = useMemo(
    () => (fullSchedule ? flattenScheduledMatches(fullSchedule) : []),
    [fullSchedule]
  );
  const dayModels = useMemo(() => {
    const cache = courtGridCacheRef.current;
    if (gridInput) {
      const day = gridInput.day;
      return [
        buildDayGridModel(
          day,
          scheduleConstraints?.days.find((constraintsDay) => constraintsDay.dayKey === day.dayKey),
          cache,
          scheduleConstraints,
          timeZone,
          allScheduledMatchesForBounds
        ),
      ];
    }
    return (
      displaySchedule?.days.map((day) =>
        buildDayGridModel(
          day,
          scheduleConstraints?.days.find((constraintsDay) => constraintsDay.dayKey === day.dayKey),
          cache,
          scheduleConstraints,
          timeZone,
          allScheduledMatchesForBounds
        )
      ) || []
    );
  }, [
    gridInput?.courtRefsKey,
    gridInput?.day,
    displaySchedule,
    scheduleConstraints,
    timeZone,
    allScheduledMatchesForBounds,
  ]);

  const swapPreviewMayNeedAllMatches =
    (placeModeActive || scheduleDragActive) &&
    (swapModifierHeld || blockInvalidPlacement) &&
    !!activePlacementMatch &&
    !!scheduleConstraints &&
    typeof onSwapPendingWithScheduled === 'function';

  const groupDragActive =
    groupDragMatchIds != null && groupDragMatchIds.length >= MIN_SCHEDULE_BULK_ADJUST;

  const needAllScheduledMatches = swapPreviewMayNeedAllMatches || groupDragActive;

  const allScheduledMatchesFlat = useMemo(
    () =>
      needAllScheduledMatches && fullSchedule
        ? flattenScheduledMatches(fullSchedule)
        : EMPTY_SCHEDULED_MATCHES,
    [fullSchedule, needAllScheduledMatches]
  );

  const conflictMatchIdSet = useMemo(() => {
    if (conflictMatchIdList.length === 0) return null;
    return new Set(conflictMatchIdList);
  }, [conflictMatchIdList]);

  const bulkSelectedMatchIdSet = useMemo(() => {
    if (selectedMatchIds.length < MIN_SCHEDULE_BULK_ADJUST) return null;
    return new Set(selectedMatchIds);
  }, [selectedMatchIds]);

  const focusedConflictPeerSet = useMemo(
    () => new Set(focusedConflictPeerIds),
    [focusedConflictPeerIds]
  );

  return (
    <SchedulePlacementActionsProvider value={placementActionsValue}>
      <SchedulePlacementSelectionProvider value={placementSelectionValue}>
      <div className="root">
        {dayModels.map((dayModel) => {
          const constraint = scheduleConstraints?.days.find(
            (d) => d.dayKey === dayModel.day.dayKey
          );
          const dayDateLabel = constraint
            ? getDateString(constraint.startTime, timeZone)
            : undefined;
          return (
            <VirtualizedDaySchedule
              key={'day-' + dayModel.day.dayKey}
              courtRefsKey={gridInput?.courtRefsKey}
              dayModel={dayModel}
              classIdToObject={classIdToObject}
              scale={scheduleItemScaleMultiplier}
              courtWidth={scheduleColumnWidth}
              timeZone={timeZone}
              dayDateLabel={dayDateLabel}
              onPlaceAtFreeSlot={onPlaceAtFreeSlot}
              focusedPlacementSlot={focusedPlacementSlot}
              focusedSwapTargetMatchId={focusedSwapTargetMatchId}
              activePlacementMatchId={activePlacementMatch?.id ?? null}
              swapPlacementMatch={activePlacementMatch}
              scheduleConstraints={scheduleConstraints ?? null}
              pendingPlacementSource={pendingPlacementSource}
              scheduleDragActive={scheduleDragActive}
              dragPlacementSource={dragPlacementSource}
              groupDragMatchIds={groupDragMatchIds}
              groupDragAnchorMatchId={groupDragAnchorMatchId}
              allScheduledMatchesFlat={allScheduledMatchesFlat}
              onSwapPendingWithScheduled={onSwapPendingWithScheduled}
              swapModifierHeld={swapModifierHeld}
              onZoomFloorsChange={onZoomFloorsChange}
              placeModeActive={placeModeActive}
              placementSessionReady={placementSessionReady}
              placementSessionIndex={placementSessionIndex}
              dragModifierBlocked={dragModifierBlocked}
              shiftModifierHeld={shiftModifierHeld}
              scheduleMutationBlocked={scheduleMutationBlocked}
              onSelectGridMatchForPlacement={onSelectGridMatchForPlacement}
              conflictMatchIdSet={conflictMatchIdSet}
              bulkSelectedMatchIdSet={bulkSelectedMatchIdSet}
              showScheduleConflicts={showScheduleConflicts}
              blockInvalidPlacement={blockInvalidPlacement}
              canonicalSchedule={fullSchedule}
              focusedConflictMatchId={focusedConflictMatchId}
              focusedConflictPeerSet={focusedConflictPeerSet}
              conflictFocusPulseGeneration={conflictFocusPulseGeneration}
            />
          );
        })}
      </div>
      </SchedulePlacementSelectionProvider>
    </SchedulePlacementActionsProvider>
  );
};

type VirtualizedCourtColumnProps = {
  courtModel: CourtGridModel;
  columnIndex: number;
  dayKey: number;
  dayStartMs: number;
  dayEndMs: number;
  scale: number;
  zoomTier: ReturnType<typeof getScheduleZoomTier>;
  effectiveCourtWidth: number;
  rowHeight: number;
  contentHeightPx: number;
  courtHeaderFontSize: number;
  visibleStartMs: number;
  visibleEndMs: number;
  showScheduledMatches: boolean;
  hasMeasuredViewport: boolean;
  placeModeActive: boolean;
  activePlacementMatchId: number | null;
  pendingPlacementSource: 'unscheduled' | 'scheduled_grid' | null;
  onPlaceAtFreeSlot?: (slot: IFreeSlotItem) => void;
  classIdToObject: Record<string, Class>;
  swapPreviewDims?: Map<number, SwapModifierPreviewDims>;
  swapKeyboardFocusMatchId: number | null;
  totalColumnHeight: number;
  gridPlacementRef: React.RefObject<GridMatchPlacementBehavior | null>;
  conflictMatchIdSet: Set<number> | null;
  bulkSelectedMatchIdSet: Set<number> | null;
  showScheduleConflicts: boolean;
  activePlacementMatchIdForConflict: number | null;
  focusedConflictMatchId: number | null;
  focusedConflictPeerSet: Set<number>;
  conflictFocusPulseGeneration: number;
  shiftModifierHeld: boolean;
  groupDragAnchorMatchId: number | null;
  groupDragOverlayLayout: GroupDragOverlayLayout | null;
  groupDragMatchIds: number[] | null;
};

function virtualizedCourtColumnPropsAreEqual(
  prev: VirtualizedCourtColumnProps,
  next: VirtualizedCourtColumnProps
): boolean {
  return (
    prev.courtModel === next.courtModel &&
    prev.columnIndex === next.columnIndex &&
    prev.zoomTier === next.zoomTier &&
    prev.effectiveCourtWidth === next.effectiveCourtWidth &&
    prev.rowHeight === next.rowHeight &&
    prev.contentHeightPx === next.contentHeightPx &&
    prev.courtHeaderFontSize === next.courtHeaderFontSize &&
    prev.visibleStartMs === next.visibleStartMs &&
    prev.visibleEndMs === next.visibleEndMs &&
    prev.showScheduledMatches === next.showScheduledMatches &&
    prev.hasMeasuredViewport === next.hasMeasuredViewport &&
    prev.placeModeActive === next.placeModeActive &&
    prev.activePlacementMatchId === next.activePlacementMatchId &&
    prev.pendingPlacementSource === next.pendingPlacementSource &&
    prev.onPlaceAtFreeSlot === next.onPlaceAtFreeSlot &&
    prev.classIdToObject === next.classIdToObject &&
    prev.swapPreviewDims === next.swapPreviewDims &&
    prev.swapKeyboardFocusMatchId === next.swapKeyboardFocusMatchId &&
    prev.totalColumnHeight === next.totalColumnHeight &&
    prev.gridPlacementRef === next.gridPlacementRef &&
    prev.conflictMatchIdSet === next.conflictMatchIdSet &&
    prev.bulkSelectedMatchIdSet === next.bulkSelectedMatchIdSet &&
    prev.showScheduleConflicts === next.showScheduleConflicts &&
    prev.activePlacementMatchIdForConflict === next.activePlacementMatchIdForConflict &&
    prev.focusedConflictMatchId === next.focusedConflictMatchId &&
    prev.focusedConflictPeerSet === next.focusedConflictPeerSet &&
    prev.conflictFocusPulseGeneration === next.conflictFocusPulseGeneration &&
    prev.shiftModifierHeld === next.shiftModifierHeld &&
    prev.groupDragAnchorMatchId === next.groupDragAnchorMatchId &&
    prev.groupDragOverlayLayout === next.groupDragOverlayLayout &&
    prev.groupDragMatchIds === next.groupDragMatchIds
  );
}

const VirtualizedCourtColumn = React.memo(function VirtualizedCourtColumn({
  courtModel,
  columnIndex,
  dayKey,
  dayStartMs,
  dayEndMs,
  scale,
  zoomTier,
  effectiveCourtWidth,
  rowHeight,
  contentHeightPx,
  courtHeaderFontSize,
  visibleStartMs,
  visibleEndMs,
  showScheduledMatches,
  hasMeasuredViewport,
  placeModeActive,
  activePlacementMatchId,
  pendingPlacementSource,
  onPlaceAtFreeSlot,
  classIdToObject,
  swapPreviewDims,
  swapKeyboardFocusMatchId,
  totalColumnHeight,
  gridPlacementRef,
  conflictMatchIdSet,
  bulkSelectedMatchIdSet,
  showScheduleConflicts,
  activePlacementMatchIdForConflict,
  focusedConflictMatchId,
  focusedConflictPeerSet,
  conflictFocusPulseGeneration,
  shiftModifierHeld,
  groupDragAnchorMatchId,
  groupDragOverlayLayout,
  groupDragMatchIds,
}: VirtualizedCourtColumnProps) {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';

  const courtSize = effectiveCourtWidth + getCourtGapPx(effectiveCourtWidth);

  const hasConflictFocusInColumn =
    focusedConflictMatchId != null &&
    courtModel.matchRanges.some(
      (range) =>
        range.item.id === focusedConflictMatchId || focusedConflictPeerSet.has(range.item.id)
    );

  const visibleMatches =
    hasMeasuredViewport && showScheduledMatches
      ? courtModel.matchRanges.filter(
          (range) => range.startMs < visibleEndMs && range.endMs > visibleStartMs
        )
      : [];

  const courtDropZoneStyle = {
    position: 'absolute',
    top: HEADER_HEIGHT,
    left: 0,
    width: effectiveCourtWidth,
    height: contentHeightPx,
  } as CSSProperties;

  return (
    <div
      className="virtualCourtColumn"
      data-zoom-tier={zoomTier}
      style={
        {
          transform: `translateX(calc(${columnIndex} * var(--court-step)))`,
          width: 'var(--court-width)',
          height: totalColumnHeight,
          '--header-height': `${HEADER_HEIGHT}px`,
          '--slot-height': `${rowHeight}px`,
          '--court-width': `${effectiveCourtWidth}px`,
          '--court-step': `${courtSize}px`,
          '--scale-y': scale,
        } as CSSProperties
      }
    >
      <h3
        className="column-header virtualCourtHeader"
        title={courtModel.courtSchedule.court.name}
        style={{
          width: effectiveCourtWidth,
          maxWidth: effectiveCourtWidth,
          fontSize: courtHeaderFontSize,
        }}
      >
        {courtModel.courtSchedule.court.name}
      </h3>
      <div
        className={
          hasConflictFocusInColumn ? 'virtualCourtInner virtualCourtInner--conflict-focus' : 'virtualCourtInner'
        }
      >
        <CourtDropZone
          id={getCourtDropZoneId(dayKey, courtModel.courtSchedule.court.id)}
          court={courtModel.courtSchedule.court}
          day={dayKey}
          dayStartMs={dayStartMs}
          dayEndMs={dayEndMs}
          scale={scale}
          placeModeActive={placeModeActive}
          onPlaceClick={onPlaceAtFreeSlot}
          courtLabel={t(prefix + 'scheduleCourtPlaceAriaLabel', {
            court: courtModel.courtSchedule.court.name,
          })}
          style={courtDropZoneStyle}
        />
        {visibleMatches.map((range) => {
          const previewDims = swapPreviewDims?.get(range.item.id);
          const placementPressed =
            pendingPlacementSource === 'scheduled_grid' &&
            activePlacementMatchId === range.item.id;
          const conflictInSet =
            conflictMatchIdSet != null && conflictMatchIdSet.has(range.item.id);
          const inFocusedConflict = focusedConflictPeerSet.has(range.item.id);
          const conflictHighlight =
            conflictInSet && (showScheduleConflicts || inFocusedConflict);
          const conflictFocused = focusedConflictMatchId === range.item.id;
          const conflictSelected =
            conflictInSet &&
            (conflictFocused ||
              (activePlacementMatchIdForConflict === range.item.id && inFocusedConflict));
          const bulkSelected =
            bulkSelectedMatchIdSet != null && bulkSelectedMatchIdSet.has(range.item.id);
          const groupDragGhostHidden =
            groupDragOverlayLayout != null &&
            groupDragOverlayLayout.items.some((entry) => entry.match.id === range.item.id);
          const groupDragOverlay =
            groupDragAnchorMatchId === range.item.id ? groupDragOverlayLayout : null;
          const groupDragDropPassthrough =
            groupDragMatchIds != null && groupDragMatchIds.includes(range.item.id);
          const minutesFromStart = getMatchMinutesFromDayStart(range.startMs, dayStartMs);
          const matchDuration = range.item.duration || 20;
          return (
            <MatchItem
              key={range.item.id}
              id={range.item.id.toString()}
              item={range.item}
              isScheduled={true}
              cssZoomLayout
              zoomTier={zoomTier}
              classObject={classIdToObject[range.item.classId]}
              gridPlacementRef={gridPlacementRef}
              placementPressed={placementPressed}
              conflictHighlight={conflictHighlight}
              conflictSelected={conflictSelected}
              conflictFocused={conflictFocused}
              bulkSelected={bulkSelected}
              shiftModifierHeld={shiftModifierHeld}
              groupDragGhostHidden={groupDragGhostHidden}
              groupDragOverlay={groupDragOverlay}
              groupDragDropPassthrough={groupDragDropPassthrough}
              conflictFocusPulseGeneration={conflictFocused ? conflictFocusPulseGeneration : undefined}
              dimInvalidSwapTarget={previewDims?.dimInvalidSwapTarget}
              ctrlSwapSubtleDim={previewDims?.ctrlSwapSubtleDim}
              swapKeyboardFocus={swapKeyboardFocusMatchId === range.item.id}
              style={{
                position: 'absolute',
                top: getGridMatchTopCalc(minutesFromStart),
                height: getGridMatchHeightCalc(matchDuration),
                width: 'var(--court-width)',
                left: 0,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}, virtualizedCourtColumnPropsAreEqual);

type VirtualizedDayScheduleProps = {
  courtRefsKey?: string;
  dayModel: DayGridModel;
  classIdToObject: Record<string, Class>;
  scale: number;
  courtWidth: number;
  timeZone: string;
  dayDateLabel?: string;
  onPlaceAtFreeSlot?: (slot: IFreeSlotItem) => void;
  focusedPlacementSlot?: IFreeSlotItem | null;
  focusedSwapTargetMatchId?: number | null;
  activePlacementMatchId: number | null;
  swapPlacementMatch?: IMatchScheduleItem | null;
  scheduleConstraints: IScheduleConstraints | null;
  onSwapPendingWithScheduled?: (targetMatch: IMatchScheduleItem) => void | Promise<void>;
  swapModifierHeld: boolean;
  scheduleDragActive: boolean;
  dragPlacementSource: 'unscheduled' | 'scheduled' | null;
  allScheduledMatchesFlat: IMatchScheduleItem[];
  pendingPlacementSource: 'unscheduled' | 'scheduled_grid' | null;
  onZoomFloorsChange?: (dayKey: number, floors: ScheduleZoomFloors) => void;
  placeModeActive: boolean;
  placementSessionReady: boolean;
  placementSessionIndex: PlacementSessionIndex | null;
  dragModifierBlocked: boolean;
  scheduleMutationBlocked: boolean;
  onSelectGridMatchForPlacement?: (matchId: number) => void;
  conflictMatchIdSet: Set<number> | null;
  bulkSelectedMatchIdSet: Set<number> | null;
  showScheduleConflicts: boolean;
  blockInvalidPlacement: boolean;
  canonicalSchedule: ITournamentSchedule | null;
  focusedConflictMatchId: number | null;
  focusedConflictPeerSet: Set<number>;
  conflictFocusPulseGeneration: number;
  shiftModifierHeld: boolean;
  groupDragMatchIds: number[] | null;
  groupDragAnchorMatchId: number | null;
};

function virtualizedDaySchedulePropsAreEqual(
  prev: VirtualizedDayScheduleProps,
  next: VirtualizedDayScheduleProps
): boolean {
  if (
    !dayGridModelPropsAreEqual(
      { dayModel: prev.dayModel, scale: prev.scale, courtWidth: prev.courtWidth },
      { dayModel: next.dayModel, scale: next.scale, courtWidth: next.courtWidth }
    )
  ) {
    return false;
  }
  if (prev.courtRefsKey !== next.courtRefsKey) return false;
  if (prev.classIdToObject !== next.classIdToObject) return false;
  if (prev.timeZone !== next.timeZone) return false;
  if (prev.dayDateLabel !== next.dayDateLabel) return false;
  if (prev.activePlacementMatchId !== next.activePlacementMatchId) return false;
  if (prev.focusedPlacementSlot !== next.focusedPlacementSlot) return false;
  if (prev.focusedSwapTargetMatchId !== next.focusedSwapTargetMatchId) return false;
  if (prev.pendingPlacementSource !== next.pendingPlacementSource) return false;
  if (prev.swapModifierHeld !== next.swapModifierHeld) return false;
  if (prev.scheduleDragActive !== next.scheduleDragActive) return false;
  if (prev.dragPlacementSource !== next.dragPlacementSource) return false;
  if (prev.placeModeActive !== next.placeModeActive) return false;
  if (prev.placementSessionReady !== next.placementSessionReady) return false;
  if (prev.placementSessionIndex !== next.placementSessionIndex) return false;
  if (prev.scheduleConstraints !== next.scheduleConstraints) return false;
  if (prev.allScheduledMatchesFlat !== next.allScheduledMatchesFlat) return false;
  if (prev.onPlaceAtFreeSlot !== next.onPlaceAtFreeSlot) return false;
  if (prev.onSwapPendingWithScheduled !== next.onSwapPendingWithScheduled) return false;
  if (prev.onZoomFloorsChange !== next.onZoomFloorsChange) return false;
  if (prev.dragModifierBlocked !== next.dragModifierBlocked) return false;
  if (prev.shiftModifierHeld !== next.shiftModifierHeld) return false;
  if (prev.scheduleMutationBlocked !== next.scheduleMutationBlocked) return false;
  if (prev.onSelectGridMatchForPlacement !== next.onSelectGridMatchForPlacement) return false;
  if (prev.conflictMatchIdSet !== next.conflictMatchIdSet) return false;
  if (prev.bulkSelectedMatchIdSet !== next.bulkSelectedMatchIdSet) return false;
  if (prev.showScheduleConflicts !== next.showScheduleConflicts) return false;
  if (prev.blockInvalidPlacement !== next.blockInvalidPlacement) return false;
  if (prev.canonicalSchedule !== next.canonicalSchedule) return false;
  if (prev.focusedConflictMatchId !== next.focusedConflictMatchId) return false;
  if (prev.focusedConflictPeerSet !== next.focusedConflictPeerSet) return false;
  if (prev.conflictFocusPulseGeneration !== next.conflictFocusPulseGeneration) return false;
  if (prev.shiftModifierHeld !== next.shiftModifierHeld) return false;
  if (prev.groupDragMatchIds !== next.groupDragMatchIds) return false;
  if (prev.groupDragAnchorMatchId !== next.groupDragAnchorMatchId) return false;
  return true;
}

const VirtualizedDaySchedule = React.memo(function VirtualizedDaySchedule({
  dayModel,
  classIdToObject,
  scale,
  courtWidth,
  timeZone,
  dayDateLabel,
  onPlaceAtFreeSlot,
  focusedPlacementSlot,
  focusedSwapTargetMatchId,
  activePlacementMatchId,
  swapPlacementMatch = null,
  scheduleConstraints,
  onSwapPendingWithScheduled,
  swapModifierHeld,
  scheduleDragActive,
  dragPlacementSource,
  allScheduledMatchesFlat,
  pendingPlacementSource,
  onZoomFloorsChange,
  placeModeActive,
  placementSessionReady,
  placementSessionIndex,
  dragModifierBlocked,
  scheduleMutationBlocked,
  onSelectGridMatchForPlacement,
  conflictMatchIdSet,
  bulkSelectedMatchIdSet,
  showScheduleConflicts,
  blockInvalidPlacement,
  canonicalSchedule,
  focusedConflictMatchId,
  focusedConflictPeerSet,
  conflictFocusPulseGeneration,
  shiftModifierHeld,
  groupDragMatchIds,
  groupDragAnchorMatchId,
}: VirtualizedDayScheduleProps) {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';
  const { isGesturing } = useScheduleZoom();
  const isGesturingRef = useRef(isGesturing);
  isGesturingRef.current = isGesturing;
  const unscheduledMatches = useAppSelector(selectUnscheduledMatches);
  const scrollRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const gridPlacementRef = useRef<GridMatchPlacementBehavior | null>(null);
  const prevGesturingRef = useRef(false);
  /** Defer MatchItem mount until grid viewport is wired (avoids dnd+layout race on heavy tournaments). */
  const [showScheduledMatches, setShowScheduledMatches] = useState(false);
  const [measuredViewportWidth, setMeasuredViewportWidth] = useState(0);

  useEffect(() => {
    setShowScheduledMatches(false);
    setMeasuredViewportWidth(0);
  }, [dayModel.day.dayKey]);

  const rowHeight = getScheduleLaidOutRowHeightPx(scale);
  const effectiveCourtWidth = getScheduleEffectiveCourtWidthPx(
    dayModel.courts.length,
    courtWidth,
    measuredViewportWidth
  );
  const courtGap = getCourtGapPx(effectiveCourtWidth);
  const courtSize = effectiveCourtWidth + courtGap;
  const courtHeaderFontSize = Math.max(10, Math.min(18, effectiveCourtWidth / 12));
  const zoomTier = getScheduleZoomTier(scale, effectiveCourtWidth);
  const deferredPlacementSessionIndex = useDeferredValue(placementSessionIndex);

  const groupDragOverlayLayout = useMemo(() => {
    if (
      !groupDragAnchorMatchId ||
      !groupDragMatchIds ||
      groupDragMatchIds.length < MIN_SCHEDULE_BULK_ADJUST ||
      !scheduleConstraints
    ) {
      return null;
    }
    const anchor = allScheduledMatchesFlat.find((m) => m.id === groupDragAnchorMatchId);
    if (!anchor) return null;
    const peerMatches = groupDragMatchIds
      .filter((id) => id !== groupDragAnchorMatchId)
      .map((id) => allScheduledMatchesFlat.find((m) => m.id === id))
      .filter((m): m is IMatchScheduleItem => m != null);
    return buildGroupDragOverlayLayout({
      anchor,
      peerMatches,
      constraints: scheduleConstraints,
      timeZone,
      scale,
      courtWidth: effectiveCourtWidth,
    });
  }, [
    groupDragAnchorMatchId,
    groupDragMatchIds,
    scheduleConstraints,
    allScheduledMatchesFlat,
    timeZone,
    scale,
    effectiveCourtWidth,
  ]);

  const rowVirtualizer = useVirtualizer({
    count: dayModel.slotCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: VERTICAL_OVERSCAN,
  });

  const columnVirtualizer = useVirtualizer({
    count: dayModel.courts.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => courtSize,
    horizontal: true,
    overscan: scheduleDragActive ? 8 : HORIZONTAL_OVERSCAN,
  });

  const courtMatchRangesRefs = useMemo(
    () => dayModel.courts.map((court) => court.matchRanges),
    [dayModel.courts]
  );

  useLayoutEffect(() => {
    if (isGesturing) return;
    columnVirtualizer.measure();
  }, [
    isGesturing,
    courtSize,
    dayModel.courts.length,
    courtMatchRangesRefs,
    columnVirtualizer,
  ]);

  useLayoutEffect(() => {
    if (isGesturing) return;
    rowVirtualizer.measure();
  }, [isGesturing, rowHeight, dayModel.slotCount, rowVirtualizer]);

  useLayoutEffect(() => {
    if (prevGesturingRef.current && !isGesturing) {
      rowVirtualizer.measure();
      columnVirtualizer.measure();
    }
    prevGesturingRef.current = isGesturing;
  }, [isGesturing, rowVirtualizer, columnVirtualizer]);

  const canvasParams = useMemo(
    () => ({
      courtCount: dayModel.courts.length,
      viewportWidth: measuredViewportWidth,
      slotCount: dayModel.slotCount,
    }),
    [dayModel.courts.length, dayModel.slotCount, measuredViewportWidth]
  );

  const syncCanvasZoomCss = useCallback(() => {
    const el = canvasRef.current;
    if (!el) return;
    applyScheduleZoomCssVars(
      el,
      scale,
      courtWidth,
      canvasParams.courtCount,
      canvasParams.viewportWidth,
      canvasParams.slotCount
    );
  }, [scale, courtWidth, canvasParams]);

  useLayoutEffect(() => {
    if (isGesturing) return;
    syncCanvasZoomCss();
  }, [isGesturing, syncCanvasZoomCss]);

  const handleCanvasRef = useCallback(
    (el: HTMLDivElement | null) => {
      if (canvasRef.current) {
        unregisterScheduleZoomCanvas(canvasRef.current);
      }
      canvasRef.current = el;
      if (el) {
        registerScheduleZoomCanvas(el, canvasParams);
        if (!isGesturing) {
          applyScheduleZoomCssVars(
            el,
            scale,
            courtWidth,
            canvasParams.courtCount,
            canvasParams.viewportWidth,
            canvasParams.slotCount
          );
        }
      }
    },
    [canvasParams, courtWidth, isGesturing, scale]
  );

  useEffect(() => {
    const el = canvasRef.current;
    if (el) {
      updateScheduleZoomCanvasParams(el, canvasParams);
    }
  }, [canvasParams]);

  useEffect(() => {
    return () => {
      if (canvasRef.current) {
        unregisterScheduleZoomCanvas(canvasRef.current);
      }
    };
  }, []);

  const virtualRows = rowVirtualizer.getVirtualItems();
  const virtualColumns = columnVirtualizer.getVirtualItems();
  const scrollEl = scrollRef.current;
  const hasMeasuredViewport = (scrollEl?.clientHeight ?? 0) > 0;
  const scrollTop = scrollEl?.scrollTop || 0;
  const viewportHeight = scrollEl?.clientHeight ?? 0;
  const visibleStartPx = hasMeasuredViewport
    ? Math.max(0, scrollTop - HEADER_HEIGHT - MATCH_RENDER_BUFFER_PX)
    : 0;
  const visibleEndPx = hasMeasuredViewport
    ? Math.min(
        rowVirtualizer.getTotalSize(),
        scrollTop - HEADER_HEIGHT + viewportHeight + MATCH_RENDER_BUFFER_PX
      )
    : 0;
  const visibleStartMs = dayModel.dayStartMs + (visibleStartPx / scale) * MS_PER_MINUTE;
  const visibleEndMs = dayModel.dayStartMs + (visibleEndPx / scale) * MS_PER_MINUTE;

  const swapContextActive = placeModeActive || scheduleDragActive;

  gridPlacementRef.current = {
    swapContextActive,
    activePlacementMatchId,
    scheduleMutationBlocked,
    placementDragDisabled: placeModeActive,
    dragModifierBlocked,
    onSelectForPlacement: onSelectGridMatchForPlacement,
    onSwapWithScheduledMatch: onSwapPendingWithScheduled,
  };

  const activePlacementMatch = useMemo(() => {
    if (activePlacementMatchId == null) return null;
    if (swapPlacementMatch?.id === activePlacementMatchId) return swapPlacementMatch;
    for (const courtModel of dayModel.courts) {
      for (const range of courtModel.matchRanges) {
        if (range.item.id === activePlacementMatchId) return range.item;
      }
    }
     return allScheduledMatchesFlat.find((m) => m.id === activePlacementMatchId) ?? null;
  }, [activePlacementMatchId, swapPlacementMatch, dayModel.courts, allScheduledMatchesFlat]);

  const swapPreviewActive =
    swapContextActive &&
    !!activePlacementMatch &&
    !!scheduleConstraints &&
    typeof onSwapPendingWithScheduled === 'function' &&
    (swapModifierHeld || blockInvalidPlacement);

  const swapPreviewDimsByCourtId = useMemo(() => {
    if (!swapPreviewActive || !activePlacementMatch || !scheduleConstraints) {
      return null;
    }

    const scheduledMatches = dayModel.courts.flatMap((courtModel) =>
      courtModel.matchRanges.map((range) => range.item)
    );

    const swapConflictIndex =
      blockInvalidPlacement && canonicalSchedule
        ? getPlacementConflictIndex(
            canonicalSchedule,
            scheduleConstraints,
            timeZone,
            [dayModel.day.dayKey],
            unscheduledMatches,
            classIdToObject
          )
        : null;

    const dimsByMatchId = buildSwapModifierPreviewDimsMap({
      swapPreviewActive,
      swapModifierHeld,
      activePlacementMatch,
      scheduleConstraints,
      scheduledMatches,
      allScheduledMatchesFlat,
      pendingPlacementSource,
      scheduleDragActive,
      dragPlacementSource,
      blockInvalidPlacement,
      canonicalSchedule,
      scheduleTimeZone: timeZone,
      classIdToObject,
      swapConflictIndex,
    });

    const byCourt = new Map<number, Map<number, SwapModifierPreviewDims>>();
    for (const courtModel of dayModel.courts) {
      const courtMap = new Map<number, SwapModifierPreviewDims>();
      for (const range of courtModel.matchRanges) {
        const dims = dimsByMatchId.get(range.item.id);
        if (dims) courtMap.set(range.item.id, dims);
      }
      byCourt.set(courtModel.courtSchedule.court.id, courtMap);
    }
    return byCourt;
  }, [
    swapPreviewActive,
    swapModifierHeld,
    activePlacementMatch,
    scheduleConstraints,
    dayModel.courts,
    allScheduledMatchesFlat,
    pendingPlacementSource,
    scheduleDragActive,
    dragPlacementSource,
    blockInvalidPlacement,
    canonicalSchedule,
    timeZone,
    classIdToObject,
    unscheduledMatches,
  ]);

  const contentHeightPx = rowVirtualizer.getTotalSize();

  const placementDayIndex = getPlacementDayIndex(
    placementSessionIndex,
    dayModel.day.dayKey
  );

  const showPlacementBands =
    placementSessionReady && (placeModeActive || scheduleDragActive);

  const courtIdsByColumnIndex = useMemo(
    () => dayModel.courts.map((c) => c.courtSchedule.court.id),
    [dayModel.courts]
  );

  const canvasWidthPx = getScheduleCanvasWidthPx(
    dayModel.courts.length,
    effectiveCourtWidth
  );
  const courtAreaWidthPx = columnVirtualizer.getTotalSize();
  const canvasHeightPx = getScheduleCanvasHeightPx(dayModel.slotCount, scale);

  useLayoutEffect(() => {
    if (isGesturingRef.current) return;
    const el = scrollRef.current;
    if (!el) return;

    clampScheduleGridScroll(el, canvasWidthPx, canvasHeightPx);
  }, [
    canvasWidthPx,
    canvasHeightPx,
    scale,
    courtWidth,
    effectiveCourtWidth,
    dayModel.day.dayKey,
    dayModel.courts.length,
    dayModel.slotCount,
  ]);

  useEffect(() => {
    if (!onZoomFloorsChange) return;

    let cancelled = false;
    let observer: ResizeObserver | undefined;
    let attachedEl: HTMLDivElement | null = null;
    let onScroll: (() => void) | undefined;

    const clampScroll = (el: HTMLElement) => {
      const layoutCourtWidth = getScheduleEffectiveCourtWidthPx(
        dayModel.courts.length,
        courtWidth,
        el.clientWidth
      );
      clampScheduleGridScroll(
        el,
        getScheduleCanvasWidthPx(dayModel.courts.length, layoutCourtWidth),
        getScheduleCanvasHeightPx(dayModel.slotCount, scale)
      );
    };

    const reportViewport = (el: HTMLElement) => {
      if (isGesturingRef.current) return;
      setMeasuredViewportWidth((prev) =>
        prev === el.clientWidth ? prev : el.clientWidth
      );
      const floors = computeScheduleZoomFloorsForDay(
        dayModel.slotCount,
        dayModel.courts.length,
        el.clientWidth,
        el.clientHeight
      );
      onZoomFloorsChange(dayModel.day.dayKey, floors);
      clampScroll(el);
    };

    const syncGrid = () => {
      if (cancelled) return;
      const el = scrollRef.current;
      if (!el || el.clientHeight <= 0) return;

      if (attachedEl !== el) {
        observer?.disconnect();
        if (attachedEl && onScroll) {
          attachedEl.removeEventListener('scroll', onScroll);
        }
        attachedEl = el;
        onScroll = () => {
          if (isGesturingRef.current) return;
          clampScroll(el);
        };
        observer = new ResizeObserver(() => reportViewport(el));
        observer.observe(el);
        el.addEventListener('scroll', onScroll, { passive: true });
      }
      reportViewport(el);
      setShowScheduledMatches(true);
    };

    const frameId = requestAnimationFrame(syncGrid);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
      observer?.disconnect();
      if (attachedEl && onScroll) {
        attachedEl.removeEventListener('scroll', onScroll);
      }
    };
  }, [
    courtWidth,
    dayModel.courts.length,
    dayModel.day.dayKey,
    dayModel.slotCount,
    onZoomFloorsChange,
    scale,
    rowHeight,
    courtSize,
  ]);

  return (
    <div className="day">
      <h1>
        {t(prefix + 'scheduleSettingsDayLabel', { day: dayModel.day.dayKey + 1 })}
        {dayDateLabel ? ` (${dayDateLabel})` : ''}
      </h1>
      <div className="daySchedule dayScheduleVirtual" ref={scrollRef}>
        <div
          ref={handleCanvasRef}
          className="scheduleVirtualCanvas"
          style={
            {
              width: canvasWidthPx,
              height: HEADER_HEIGHT + rowVirtualizer.getTotalSize(),
              '--header-height': `${HEADER_HEIGHT}px`,
              '--slot-height': `${rowHeight}px`,
              '--court-width': `${effectiveCourtWidth}px`,
              '--court-step': `${courtSize}px`,
              '--scale-y': scale,
            } as React.CSSProperties
          }
        >
          <div
            className="virtualTimeAxis"
            style={{
              width: TIME_COLUMN_WIDTH,
              height: HEADER_HEIGHT + rowVirtualizer.getTotalSize(),
            }}
          >
            <div className="virtualCornerHeader">{t(prefix + 'scheduleTimeAxisLabel')}</div>
            {virtualRows.map((virtualRow) => {
              const slotStart = new Date(
                dayModel.dayStartMs + virtualRow.index * FREE_SLOT_MINUTES * MS_PER_MINUTE
              ).toISOString();

              return (
                <div
                  className="timeItem virtualTimeItem"
                  key={'time-' + virtualRow.key}
                  style={
                    {
                      height: 'var(--slot-height)',
                      minHeight: 'var(--slot-height)',
                      top: `calc(var(--header-height) + ${virtualRow.index} * var(--slot-height))`,
                      '--scale-y': scale,
                    } as React.CSSProperties
                  }
                >
                  {virtualRow.index % 3 === 0
                    ? formatSlotTimeLabel(slotStart, dayModel.constraintDayDateStr, timeZone)
                    : ''}
                </div>
              );
            })}
          </div>
          <div
            className="virtualCourtArea"
            style={{
              left: TIME_COLUMN_WIDTH,
              width: courtAreaWidthPx,
              height: HEADER_HEIGHT + rowVirtualizer.getTotalSize(),
            }}
          >
            {virtualColumns.map((virtualColumn) => {
              const courtModel = dayModel.courts[virtualColumn.index];

              return (
                <VirtualizedCourtColumn
                  key={courtModel.courtSchedule.court.id}
                  courtModel={courtModel}
                  columnIndex={virtualColumn.index}
                  dayKey={dayModel.day.dayKey}
                  dayStartMs={dayModel.dayStartMs}
                  dayEndMs={dayModel.dayEndMs}
                  scale={scale}
                  zoomTier={zoomTier}
                  effectiveCourtWidth={effectiveCourtWidth}
                  rowHeight={rowHeight}
                  contentHeightPx={contentHeightPx}
                  courtHeaderFontSize={courtHeaderFontSize}
                  visibleStartMs={visibleStartMs}
                  visibleEndMs={visibleEndMs}
                  showScheduledMatches={showScheduledMatches}
                  hasMeasuredViewport={hasMeasuredViewport}
                  placeModeActive={placeModeActive}
                  activePlacementMatchId={activePlacementMatchId}
                  pendingPlacementSource={pendingPlacementSource}
                  onPlaceAtFreeSlot={onPlaceAtFreeSlot}
                  classIdToObject={classIdToObject}
                  swapPreviewDims={swapPreviewDimsByCourtId?.get(
                    courtModel.courtSchedule.court.id
                  )}
                  swapKeyboardFocusMatchId={focusedSwapTargetMatchId ?? null}
                  totalColumnHeight={HEADER_HEIGHT + rowVirtualizer.getTotalSize()}
                  gridPlacementRef={gridPlacementRef}
                  conflictMatchIdSet={conflictMatchIdSet}
                  bulkSelectedMatchIdSet={bulkSelectedMatchIdSet}
                  showScheduleConflicts={showScheduleConflicts}
                  activePlacementMatchIdForConflict={activePlacementMatchId}
                  focusedConflictMatchId={focusedConflictMatchId}
                  focusedConflictPeerSet={focusedConflictPeerSet}
                  conflictFocusPulseGeneration={conflictFocusPulseGeneration}
                  shiftModifierHeld={shiftModifierHeld}
                  groupDragAnchorMatchId={groupDragAnchorMatchId}
                  groupDragOverlayLayout={groupDragOverlayLayout}
                  groupDragMatchIds={groupDragMatchIds}
                />
              );
            })}
            {placementSessionReady && (showPlacementBands || focusedPlacementSlot) ? (
              <SchedulePlacementOverlay
                virtualColumns={virtualColumns}
                placementDayIndex={getPlacementDayIndex(
                  deferredPlacementSessionIndex,
                  dayModel.day.dayKey
                )}
                showPlacementBands={showPlacementBands}
                headerHeight={HEADER_HEIGHT}
                contentHeightPx={contentHeightPx}
                rowHeight={rowHeight}
                scale={scale}
                dayStartMs={dayModel.dayStartMs}
                slotMinutes={FREE_SLOT_MINUTES}
                dayKey={dayModel.day.dayKey}
                focusedPlacementSlot={focusedPlacementSlot}
                courtIdsByColumnIndex={courtIdsByColumnIndex}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}, virtualizedDaySchedulePropsAreEqual);
VirtualizedDaySchedule.displayName = 'VirtualizedDaySchedule';
